/**
 * AJAX Middleware
 *
 * When making AJAX calls, we should pass in the following object on the action:
 * action = {
 * 	type: CONSTANT_ACTION_TYPE,
 * 	AJAX: {
 * 		url:     "string" - required,
 * 		success: "string" - optional string for the next ACTION Type run upon success
 * 		error:   "string" - optional string for the next ACTION type run upon failure
 * 		cancel:  "string" - optional string for the next ACTION type run upon cancel, when `cancelToken` is provided
 * 		cancelToken: axios.CancelToken.source().token instance used to cancel requests (with `source.cancel()`)
 * 	}
 * }
 *
 * If the success, or the error strings aren't given, then the same action name will be used
 *
 * This will allow the resulting middleware to make the AJAX request for us
 * 	This:
 * 		* Saves boiler plate on AJAX
 * 		* Allows us to drop in an AJAX replacement if necessary
 * 		* Handle all error & analytics tracking in one place
 */
import React from "react";
import axios from "axios";
// import { errorLog } from "utils/Rave";
import { NOTIFICATION_ACTIONS } from "reducers/Notifications";
import {
	API_URL
	, UI_URL
	, LS_REDIRECT
} from "utils/constants";
import { getToken, setToken } from "utils/Token";

import { TheHighGround } from "rhombus-react";

const UNAUTHORIZED = 401
	, NO_ACCESS = 403
	// , TEN = 10000
	, BASE_AJAX = {
		method:"get",
		version:1,
		apiType:"main",
		gateway:"crm",
		withCredentials:true,
	};

function _setToken(data){
	try {
		if(data.result.headers["x-csrf-token"]){
			setToken(data.result.headers["x-csrf-token"]);
		}
	} catch (err){
		console.info("Error Saving CSRF Token");
		// TODO: log sentry
	}
}


function errorLog(){}

function AJAXMiddleware(store){
	return function(next){
		return function(action){
			if(!action.AJAX) { return next(action); }

			var axiosReq = Object.assign({}, BASE_AJAX, action.AJAX, {
					headers: {
						...action.AJAX.headers,
						Accept:"*/*",
						"x-requested-with":"XMLHttpRequest",
						"x-csrf-token":getToken(),
					},
				})
				, baseURL = ""
				;

			// Gateway locations need the gateway prefix prior to vXXX
			if(axiosReq.gateway){
				baseURL = `${API_URL}/${axiosReq.gateway}/`;
			} else {
				baseURL = `${API_URL}/`;
			}

			if(axiosReq.version === 0){
				axiosReq.url = `${baseURL}${axiosReq.url}`;
			} else {
				axiosReq.url = `${baseURL}v${axiosReq.version}/${axiosReq.url}`;
			}

			action.meta = {
				suppressCancelGrowl: true,
				...action.meta,
				timing:{
					label: axiosReq.url.replace(/(\/\d{1,17}\/)/gi, "/MASKED_ID/"),
					start: new Date().getTime(),
				},
			};

			function makeAction(ready, success = null, data = { result: { data: null } }){
				let newAction = {
					...action,
					success,
					payload: {
						...data.result,
						original:action.payload,
					},
				};

				if(!ready){ return newAction; }
				_setToken(data);

				if(newAction?.meta?.suppressHighGround){
					// This should only happen once, on load of the application
					// All other times we should send messages through
					console.info("I have the High Ground");
				} else {
					TheHighGround.sendMessage("Success, resetting API token", "continue", next);
				}

				// On success of AJAX calls, any tracking Middleware can get called twice
				if(ready && newAction.meta && newAction.meta.tracking){
					newAction.meta.tracking = null;
				}

				// Finish the timing now, so that we don't do too much unnecessary work
				// if(action.meta && action.meta.timing){ calculateTiming(action.meta.timing); }

				if(success) {
					if(data.result.headers["x-csrf-token"]){
						setToken(data.result.headers["x-csrf-token"]);
					}
					newAction.type = action.AJAX.success;

					if(action.payload) {
						newAction.payload = {
							...data.result,
							original: action.payload,
						};
					}

					// Send a growl message automatically
					buildSuccessMessage(data, action, next);
				} else if(!success) {
					try {
						if(axios.isCancel(data.error)){
							/*
								You can use `response.isCanceled` from your promise.then((response) => {...})
								to respond appropriately, similarly to how you'd check `response.success`
							*/
							newAction.isCanceled = true;
							newAction.type = action.AJAX.cancel || "AJAX_CANCELED";
							newAction.payload = {
								message: data.error.message,
								original: action.payload,
							};
							return newAction;
						}

						if(action.AJAX.error) {
							newAction.type = action.AJAX.error;
						}

						newAction.payload = {
							error:data.error,
							original:action.payload,
							errorObj:data.error.response.data,
						};
					} catch (err){
						console.warn("Problem with the API");
						console.error(err);
					}
				}

				return newAction;
			}	// end makection()

			next(makeAction(false));

			return axios.request(axiosReq)
				.then(
					resp => next(makeAction(true, true, { result: resp })),
					(error) => {
						if(error.response && error.response.status) {
							if(error.response.status === UNAUTHORIZED) {
								localStorage.setItem(
									LS_REDIRECT,
									JSON.stringify({
										url: window.location.href,
										ttl: Date.now(),
									})
								);
								window.location.href = `${API_URL}/uilogin?uri=${UI_URL}`;
								return;
							}
						}	//end if(error.response.status)

						if(error.response && error.response.status === NO_ACCESS){
							console.warn("Error from the API");
						} else {
							// We should log the error to Raven
							errorLog({
								error,
								console: true,
								context: { action },
							});
						}

						const { meta } = action;
						if(axios.isCancel(error)){
							if(!meta.suppressCancelGrowl){
								next({
									type:    NOTIFICATION_ACTIONS.ADD_NOTIFICATION,
									payload: {
										type:"error",
										id: action.AJAX.url,
										message: error.message,
										messageHeader:"Canceled",
									},
								});
							}
						} else {
							if(!meta.suppressErrorGrowl) {
								buildErrorMessage(error, action, next);
							}
						}

						return next(makeAction(true, false, { error }));
					}
				);
		};
	};
}	// end AJXMiddleware()

const MAX_GROWL = 7000000;
/**
 * [buildSuccessMessage description]
 * 		Builds a success message from the API
 * 		Passes through meta info
 *
 * @param  {object}   obj    The success object from the API
 * @param  {object}   action The Action object passed to Redux
 * @param  {Function} next   next() function for dispatch
 * @return {null}          Nothing
 */
function buildSuccessMessage(obj, action, next){
	let messagePayload = {
		type:"success",
		id:Math.floor(Math.random() * MAX_GROWL),
		messageHeader:"Success",
		message:"",
	};

	if(action.meta){
		// If we don't want a growl for whatever reason
		if(action.meta.suppressGrowl){ return; }

		if(action.meta.buildMessage){
			messagePayload = action.meta.buildMessage(obj.result, true);
		}
	}

	if(!messagePayload){ return; }

	next({
		type:"AJAX_NOTIFY",
		meta:{
			// By sending through a meta object, we can make use of dispatching multiple messages
			// 	When action.meta.buildMessage() returns an array, multipl messages will be shown
			notify:messagePayload,
		},
	});
}

/**
 * [buildErrorMessage description]
 * 		Builds an error message from the given API response
 * 		Passes through the given buildMessage() function
 *
 * @param  {object}   error  Error object from the API
 * @param  {object}   action Action object passed to Redux
 * @param  {Function} next   next() function for next dispatch
 * @return {null}          Nothing
 */
function buildErrorMessage(error, action, next){
	let messagePayload = {
		type:"error",
		id: action.AJAX.url,
		message:(/delete|put|post/gi.test(action.AJAX.method)) ? "Error saving item" : "Error retrieving item",
		messageHeader:"Error!",
	};

	if(error.response && error.response.data) {
		if(error.response.data.name) { messagePayload.messageHeader = error.response.data.name; }
		if(error.response.data.description) { messagePayload.message = error.response.data.description; }
		// TODO: Remove after RRACT-239 is merged in (currently targeting Rhombus 2.8.1)
		if(error.response.data.data?.length) {
			messagePayload.message = error.response.data.data.map(message => (<div key={message}>{message}</div>));
		}
	}

	if(action?.meta?.buildMessage) {
		messagePayload = action.meta.buildMessage(error.response, false);
	}

	if(!messagePayload){ return; }

	next({
		type:    NOTIFICATION_ACTIONS.ADD_NOTIFICATION,
		payload: messagePayload,
	});
}

export const PRIVATE = {
	buildSuccessMessage,
	buildErrorMessage,
	setToken,
};

export default AJAXMiddleware;
